/* ─────────────────────────────────────────────────────────────────────────────
 * Approval Center / Inbox
 *
 * A centralised, enterprise-style inbox where every request that needs action
 * surfaces in one place — attendance corrections, leave, overtime and (in the
 * future) any module that registers an adapter in `requestRegistry`.
 *
 * It reuses existing, unchanged APIs through the registry and leaves the
 * original Korrigjime Prezence module (`/attendance-corrections`) fully intact.
 *
 * Leave is owned here. The duplicate "Kërkesat për pushim" (`/holidays`) and
 * "Miratimi i pushimeve" (`/leave-approvals`) modules were retired and now
 * redirect here; this inbox lists the same GET /api/holidays feed and approves
 * through the same two-stage /api/holidays/:id/{supervisor,hr}-decision API.
 * Submitting a new leave still opens the full form at /holidays/addholiday.
 * ──────────────────────────────────────────────────────────────────────────── */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Stack, Typography, Button, IconButton, TextField, InputAdornment,
  Skeleton, Snackbar, Alert, Tooltip, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel,
  Menu, MenuItem, ListItemIcon, useMediaQuery,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TablePagination, TableSortLabel,
  FormControl, InputLabel, Select,
} from '@mui/material';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import InsertChartRoundedIcon from '@mui/icons-material/InsertChartRounded';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import GridOnRoundedIcon from '@mui/icons-material/GridOnRounded';
import ViewKanbanRoundedIcon from '@mui/icons-material/ViewKanbanRounded';
import TableRowsRoundedIcon from '@mui/icons-material/TableRowsRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchIcon from '@mui/icons-material/Search';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import MoveToInboxRoundedIcon from '@mui/icons-material/MoveToInboxRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import LabelRoundedIcon from '@mui/icons-material/LabelRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SupervisorAccountRoundedIcon from '@mui/icons-material/SupervisorAccountRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import AdminPanelSettingsRoundedIcon from '@mui/icons-material/AdminPanelSettingsRounded';
import RuleRoundedIcon from '@mui/icons-material/RuleRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';

import * as XLSX from 'xlsx-js-style';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  TABS, TAB_STATUSES, TYPES, TYPE_BY_ID, CATEGORIES, categoryOf,
  fetchAllRequests, loadWorkerDirectory, getInboxContext, fetchLeaveAuditLog,
} from './requestRegistry';
import CreateRequestDialog from './CreateRequestDialog';
import StatusChip from '../common/StatusChip';

/* ── Tokens (kept local so the module is self-contained) ─────────────────────── */
const T = {
  brand: '#3D8FA3', ink: '#0F172A', ink700: '#334155', ink500: '#64748B', ink400: '#94A3B8', ink300: '#CBD5E1',
  line: '#E2E8F0', lineSoft: '#EEF2F6', surface: '#F8FAFC', white: '#FFFFFF',
};
// `fg` is the text tone and must clear 4.5:1 on its own `bg` — these labels are
// 0.66rem. success/error were previously #059669 / #DC2626, which measure 3.58
// and 4.41 against their tints and fail AA.
const TONE = {
  warning: { bg: '#FEF3C7', fg: '#92400E', dot: '#F59E0B' },
  info:    { bg: '#E7F4F7', fg: '#1F6B7E', dot: '#3D8FA3' },
  success: { bg: '#ECFDF5', fg: '#047857', dot: '#10B981' },
  error:   { bg: '#FEF2F2', fg: '#B91C1C', dot: '#EF4444' },
  neutral: { bg: '#F1F5F9', fg: '#546274', dot: '#94A3B8' },
};

const relTime = (v) => {
  if (!v) return '';
  const diff = Date.now() - new Date(v).getTime();
  const sec = Math.floor(diff / 1000); if (sec < 60) return 'tani';
  const min = Math.floor(sec / 60); if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60); if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24); if (day < 7) return `${day}d`;
  return new Date(v).toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit' });
};
const initials = (n = '') => String(n).trim().split(' ').filter(Boolean).map((x) => x[0]).join('').slice(0, 2).toUpperCase() || '?';
const dateTime = (v) => (!v ? '—' : new Date(v).toLocaleString('sq-AL', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }));
const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
// Default date range = the current calendar YEAR (01.01 → 31.12).
//
// It used to be the current month, which hid most of the inbox: a request
// submitted in July disappeared from the list on 1 August, even though it was
// still waiting for a decision. The year is the period the rest of the app
// reports on (leave balance, evaluations), so the two now agree.
const currentYearRange = () => {
  const y = new Date().getFullYear();
  return { from: ymd(new Date(y, 0, 1)), to: ymd(new Date(y, 11, 31)) };
};

/* Small circular initials avatar reused across the panel */
function Avatar({ name, size = 22, color = T.brand }) {
  return (
    <Box sx={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      bgcolor: `${color}1A`, color, fontWeight: 800, fontSize: size * 0.4,
    }}>{initials(name)}</Box>
  );
}

/* ── Small presentational atoms ──────────────────────────────────────────────── */
/* Delegates to the app-wide StatusChip so an approval status looks and reads the
 * same here, on the presence board and in the employee dashboard. Each status
 * carries its own icon, so the four states stay distinguishable in greyscale and
 * for colour-blind approvers — a dot alone only differed by hue. */
function StatusPill({ status, label }) {
  return <StatusChip status={status} label={label} size="sm" />;
}

function TypeIcon({ typeId, size = 38 }) {
  const def = TYPE_BY_ID[typeId];
  const Icon = def?.Icon || MoveToInboxRoundedIcon;
  const color = def?.color || T.ink500;
  return (
    <Box sx={{ width: size, height: size, borderRadius: '10px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${color}14`, color }}>
      <Icon sx={{ fontSize: size * 0.5 }} />
    </Box>
  );
}

/* ── Detail panel ────────────────────────────────────────────────────────────── */
function DetailPanel({ item, ctx, busy, onAction, onBack, t }) {
  const [dialog, setDialog] = useState(null); // 'approve' | 'reject' | 'requestInfo' | null
  useEffect(() => { setDialog(null); }, [item?.uid]);

  const def = TYPE_BY_ID[item.type];
  const canActInline = !!def?.actions && ctx.isManager && (item.status === 'for_approval' || item.status === 'pending');
  const isFinal = item.status === 'approved' || item.status === 'rejected';

  // Full decision history, absorbed from the retired "Miratimi i pushimeve"
  // module. Fetched per-request (one row at a time) rather than with the list,
  // so opening the inbox stays a single round trip. Falls back to the summary
  // history already on the item when the log is empty or forbidden.
  const [auditLog, setAuditLog] = useState(null);
  useEffect(() => {
    let alive = true;
    setAuditLog(null);
    if (item?.type === 'leave_request' && item?.id) {
      fetchLeaveAuditLog(item.id).then((log) => { if (alive) setAuditLog(log); });
    }
    return () => { alive = false; };
  }, [item?.type, item?.id]);
  const timeline = (auditLog && auditLog.length) ? auditLog : item.history;

  // Generic approval flow: rich multi-step where the type defines it, else a
  // single Decision step derived from the request's real status.
  const stages = useMemo(() => {
    if (def?.stages) return def.stages(item) || [];
    const st = item.status === 'approved' ? 'approved' : item.status === 'rejected' ? 'rejected' : 'pending';
    return [{ role: 'decision', status: st, at: null }];
  }, [def, item]);
  const namedFlow = !!def?.stages; // only multi-role flows name an "approver"
  const currentStage = namedFlow ? (stages.find((s) => s.status === 'pending') || null) : null;

  const confirmAction = async (mode, payload) => {
    await onAction(mode, payload);
    setDialog(null);
  };

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      {/* Header */}
      <Box sx={{ p: 1.5, borderBottom: `1px solid ${T.lineSoft}` }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <TypeIcon typeId={item.type} size={38} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontSize: '0.98rem', fontWeight: 800, color: T.ink, letterSpacing: '-0.01em' }}>{item.title}</Typography>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.125 }} useFlexGap flexWrap="wrap">
              <Typography sx={{ fontSize: '0.74rem', color: T.ink500 }}>{t(`inbox.types.${item.type}`)}</Typography>
              {item.refNumber && (
                <>
                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: T.ink300 }} />
                  <Typography sx={{ fontSize: '0.7rem', color: T.ink400, fontFamily: 'monospace' }}>{item.refNumber}</Typography>
                </>
              )}
              {currentStage && (
                <>
                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: T.ink300 }} />
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ px: 0.75, py: 0.125, borderRadius: '6px', bgcolor: TONE.warning.bg }}>
                    <HourglassEmptyRoundedIcon sx={{ fontSize: 12, color: TONE.warning.dot }} />
                    <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: TONE.warning.fg }}>{stageLabel(currentStage, t)}</Typography>
                  </Stack>
                </>
              )}
            </Stack>
          </Box>
          <Box sx={{ flexShrink: 0 }}><StatusPill status={item.status} label={t(`inbox.status.${item.status}`)} /></Box>
          <IconButton onClick={onBack} size="small" sx={{ flexShrink: 0, color: T.ink400, '&:hover': { color: T.ink, bgcolor: T.surface } }}><CloseRoundedIcon fontSize="small" /></IconButton>
        </Stack>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 1.5 }}>
        {/* Info grid — auto-fits columns to width, so it stays compact yet flexible */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(auto-fit, minmax(150px, 1fr))' }, columnGap: 1.75, rowGap: 1.25, mb: 1.75 }}>
          <InfoField label={t('inbox.detail.worker')} icon={<Avatar name={item.workerName} size={26} />}>
            <Typography noWrap sx={{ fontSize: '0.86rem', fontWeight: 700, color: T.ink }}>{item.workerName}</Typography>
            {item.department && <Typography noWrap sx={{ fontSize: '0.72rem', color: T.ink500 }}>{item.department}</Typography>}
          </InfoField>

          <InfoField
            label={t('inbox.detail.requestType')}
            icon={<Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: TYPE_BY_ID[item.type]?.color || T.brand }} />}
          >
            <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: T.ink }}>{t(`inbox.types.${item.type}`)}</Typography>
          </InfoField>

          {item.meta.map((m, i) => (
            <InfoField key={i} label={m.label} icon={<CalendarMonthRoundedIcon sx={{ fontSize: 18, color: T.ink400 }} />}>
              <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: T.ink }}>{String(m.value)}</Typography>
            </InfoField>
          ))}

          {item.createdAt && (
            <InfoField label={t('inbox.detail.submittedAt')} icon={<AccessTimeRoundedIcon sx={{ fontSize: 18, color: T.ink400 }} />}>
              <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: T.ink }}>{dateTime(item.createdAt)}</Typography>
            </InfoField>
          )}

          <InfoField label={t('inbox.detail.status')} icon={<LabelRoundedIcon sx={{ fontSize: 18, color: T.ink400 }} />}>
            <StatusPill status={item.status} label={t(`inbox.status.${item.status}`)} />
            {(item.status === 'for_approval' || item.status === 'pending') && (
              <Typography sx={{ fontSize: '0.72rem', color: T.ink500, mt: 0.375 }}>{t('inbox.detail.requiresAction')}</Typography>
            )}
          </InfoField>
        </Box>

        {/* Reason — always rendered, including when empty. An approver must be
            able to tell "no reason was given" apart from "the reason section
            isn't here", because those lead to different decisions. */}
        <Box sx={{ mb: 1.75 }}>
          <SectionLabel>{t('inbox.detail.noteReason')}</SectionLabel>
          <Box sx={{
            p: 1.25, borderRadius: '10px', bgcolor: T.surface,
            border: `1px solid ${item.description ? T.line : '#FDE68A'}`,
          }}>
            {item.description ? (
              <Typography sx={{ fontSize: '0.82rem', color: T.ink700, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {item.description}
              </Typography>
            ) : (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <HelpOutlineRoundedIcon sx={{ fontSize: 16, color: TONE.warning.dot }} />
                <Typography sx={{ fontSize: '0.82rem', color: TONE.warning.fg, fontWeight: 600 }}>
                  {t('inbox.noReason')}
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>

        {/* Attachment — the sick-leave medical certificate. An approver must be
            able to open the evidence before deciding, so this sits above the
            approval flow rather than in the info grid. */}
        {item.attachmentUrl && (
          <Box sx={{ mb: 1.75 }}>
            <SectionLabel>{t('inbox.detail.attachment', 'Dokumenti i bashkangjitur')}</SectionLabel>
            <Button
              component="a" href={item.attachmentUrl} target="_blank" rel="noopener noreferrer"
              startIcon={<AttachFileRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: 'none', fontWeight: 700, fontSize: '0.8rem',
                color: TONE.warning.fg, bgcolor: TONE.warning.bg,
                border: `1px solid ${TONE.warning.dot}40`, borderRadius: '10px', px: 1.25,
                '&:hover': { bgcolor: TONE.warning.bg, filter: 'brightness(0.96)' },
              }}>
              {t('inbox.detail.openAttachment', 'Hap fletën mjekësore')}
            </Button>
          </Box>
        )}

        {/* Approval flow — generic stepper for every request type */}
        <ApprovalFlow stages={stages} t={t} />

        {/* Activity timeline — chronological events alongside the stepper */}
        {timeline.length > 0 && (
          <Box sx={{ mt: 1.75 }}>
            <ActivityTimeline history={timeline} title={t('inbox.detail.history')} t={t} />
          </Box>
        )}
      </Box>

      {/* Footer actions */}
      <Box sx={{ p: 1.5, borderTop: `1px solid ${T.lineSoft}`, bgcolor: T.surface }}>
        {canActInline ? (
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined" fullWidth disabled={busy}
              startIcon={<BlockRoundedIcon />}
              onClick={() => setDialog('reject')}
              sx={{ color: TONE.error.fg, borderColor: '#FECACA', py: 1, '&:hover': { borderColor: TONE.error.dot, bgcolor: '#FEF2F2' }, fontWeight: 700 }}
            >
              {t('inbox.actions.reject')}
            </Button>
            <Button
              variant="contained" fullWidth disableElevation disabled={busy}
              startIcon={<CheckRoundedIcon />}
              onClick={() => setDialog('approve')}
              sx={{ bgcolor: T.brand, py: 1, '&:hover': { bgcolor: '#327a8c' }, fontWeight: 700 }}
            >
              {t('inbox.actions.approve')}
            </Button>
            {def?.actions?.requestInfo && (
              <Tooltip title={t('inbox.actions.requestInfo')}>
                <span>
                  <IconButton disabled={busy} onClick={() => setDialog('requestInfo')} sx={{ border: `1px solid ${T.line}`, borderRadius: '8px' }}>
                    <HelpOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        ) : (
          <Stack spacing={1}>
            <Typography sx={{ fontSize: '0.76rem', color: T.ink500, textAlign: 'center' }}>
              {isFinal ? t('inbox.actions.finalized') : t('inbox.actions.noAction')}
            </Typography>
          </Stack>
        )}
      </Box>

      <ActionDialog
        open={!!dialog} mode={dialog} item={item} busy={busy}
        onClose={() => setDialog(null)} onConfirm={confirmAction} t={t}
      />
    </Stack>
  );
}

function SectionLabel({ children }) {
  return <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.ink400, mb: 0.75 }}>{children}</Typography>;
}
/* Labeled field cell used inside the detail info grid */
function InfoField({ label, icon, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: T.ink400, mb: 0.375 }}>{label}</Typography>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
        {icon && <Box sx={{ display: 'flex', flexShrink: 0 }}>{icon}</Box>}
        <Box sx={{ minWidth: 0 }}>{children}</Box>
      </Stack>
    </Box>
  );
}

/* Visual treatment per stage state */
const STAGE_VIS = {
  approved: { dot: TONE.success.dot, fg: TONE.success.fg, soft: false, Icon: CheckRoundedIcon },
  rejected: { dot: TONE.error.dot,   fg: TONE.error.fg,   soft: false, Icon: CloseRoundedIcon },
  pending:  { dot: TONE.warning.dot, fg: TONE.warning.fg, soft: false, Icon: HourglassEmptyRoundedIcon },
  locked:   { dot: T.ink300,         fg: T.ink400,        soft: true,  Icon: LockRoundedIcon },
  blocked:  { dot: T.ink300,         fg: T.ink400,        soft: true,  Icon: LockRoundedIcon },
};
const ROLE_ICON = {
  supervisor: SupervisorAccountRoundedIcon, teamLeader: SupervisorAccountRoundedIcon,
  hr: BadgeRoundedIcon, finance: AccountBalanceRoundedIcon,
  itAdmin: AdminPanelSettingsRoundedIcon, reviewer: RuleRoundedIcon, decision: RuleRoundedIcon,
};
/* Resolve a stage's display label: explicit label wins, else a translated role,
 * else the raw role string. Keeps the stepper generic for any approval chain. */
const stageLabel = (stage, t) => stage.label || t(`inbox.flow.${stage.role}`, { defaultValue: stage.role || '' });

/* One rung of a generic approval ladder (Team Leader → HR → Finance → …) */
function StageRow({ stage, index, last, single, t }) {
  const vis = STAGE_VIS[stage.status] || STAGE_VIS.locked;
  const RoleIcon = ROLE_ICON[stage.role] || RuleRoundedIcon;
  const isCurrent = stage.status === 'pending';
  return (
    <Stack direction="row" spacing={1.25} sx={{ pb: last ? 0 : 1.125 }}>
      {/* Node + connector */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box sx={{
          width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: vis.soft ? '#F1F5F9' : `${vis.dot}1A`, color: vis.dot,
          border: isCurrent ? `2px solid ${vis.dot}` : '2px solid transparent',
        }}>
          <vis.Icon sx={{ fontSize: 14 }} />
        </Box>
        {!last && <Box sx={{ width: '2px', flex: 1, minHeight: 12, bgcolor: T.line, mt: 0.375 }} />}
      </Box>
      {/* Content */}
      <Box sx={{ minWidth: 0, flex: 1, pt: 0.125 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
            <RoleIcon sx={{ fontSize: 16, color: T.ink400 }} />
            <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 700, color: vis.soft ? T.ink400 : T.ink }}>
              {stageLabel(stage, t)}
            </Typography>
            {!single && (
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, color: T.ink400, bgcolor: '#EEF2F6', px: 0.625, py: 0.125, borderRadius: '999px', flexShrink: 0 }}>
                {t('inbox.flow.step', { n: index + 1 })}
              </Typography>
            )}
          </Stack>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.875, py: 0.25, borderRadius: '6px', bgcolor: vis.soft ? '#F1F5F9' : `${vis.dot}1A`, flexShrink: 0 }}>
            <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: vis.fg, whiteSpace: 'nowrap' }}>{t(`inbox.flow.state.${stage.status}`)}</Typography>
          </Box>
        </Stack>
        <Typography sx={{ fontSize: '0.7rem', color: isCurrent ? TONE.warning.fg : T.ink400, mt: 0.25, fontWeight: isCurrent ? 600 : 400 }}>
          {isCurrent ? t('inbox.flow.awaitingThisStep') : (stage.at ? new Date(stage.at).toLocaleString('sq-AL') : t(`inbox.flow.hint.${stage.status}`))}
        </Typography>
        {stage.note && <Typography sx={{ fontSize: '0.74rem', color: T.ink500, mt: 0.25, fontStyle: 'italic' }}>“{stage.note}”</Typography>}
      </Box>
    </Stack>
  );
}

/* Chronological event timeline (submission + decisions). */
function ActivityTimeline({ history, title, t }) {
  return (
    <Box sx={{ border: `1px solid ${T.line}`, borderRadius: '10px', p: 1.375, bgcolor: T.surface }}>
      <SectionLabel>{title}</SectionLabel>
      {history.length === 0 && <Typography sx={{ fontSize: '0.78rem', color: T.ink400 }}>{t('inbox.detail.noHistory')}</Typography>}
      {history.map((h, i) => {
        const last = i === history.length - 1;
        return (
          <Stack key={i} direction="row" spacing={1.25} sx={{ pb: last ? 0 : 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.25 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: T.brand, flexShrink: 0 }} />
              {!last && <Box sx={{ width: '2px', flex: 1, bgcolor: T.line, mt: 0.25 }} />}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: T.ink700 }}>{h.label}</Typography>
              <Typography sx={{ fontSize: '0.7rem', color: T.ink400 }}>
                {[h.by, h.at && new Date(h.at).toLocaleString('sq-AL')].filter(Boolean).join(' · ')}
              </Typography>
              {h.note && <Typography sx={{ fontSize: '0.74rem', color: T.ink500, mt: 0.25, fontStyle: 'italic' }}>“{h.note}”</Typography>}
            </Box>
          </Stack>
        );
      })}
    </Box>
  );
}

/* Framed approval flow — a generic staged ladder (1..N steps). Works for any
 * request type: rich multi-step where defined, a single Decision step otherwise. */
function ApprovalFlow({ stages, t }) {
  const single = stages.length === 1;
  return (
    <Box sx={{ border: `1px solid ${T.line}`, borderRadius: '10px', p: 1.375, bgcolor: T.surface }}>
      <SectionLabel>{t('inbox.detail.approvalFlow')}</SectionLabel>
      {stages.map((st, i) => (
        <StageRow key={st.role || i} stage={st} index={i} last={i === stages.length - 1} single={single} t={t} />
      ))}
    </Box>
  );
}

/* Confirmation modal for approve / reject / requestInfo — keeps every action
 * on this page (no navigation). Reject requires a reason. */
function ActionDialog({ open, mode, item, busy, onClose, onConfirm, t }) {
  const [text, setText] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => { if (open) { setText(''); setConfirmed(false); } }, [open, mode]);
  if (!mode) return null;

  const isReject = mode === 'reject';
  const isApprove = mode === 'approve';
  const reasonRequired = isReject;
  const canConfirm = !busy && confirmed && (!reasonRequired || text.trim().length > 0);

  const accent = isReject ? TONE.error : isApprove ? { dot: T.brand, fg: T.brand } : TONE.info;
  const Icon = isReject ? BlockRoundedIcon : isApprove ? CheckRoundedIcon : HelpOutlineRoundedIcon;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: `${accent.dot}1A`, color: accent.dot, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: T.ink }}>{t(`inbox.modal.${mode}.title`)}</Typography>
            <Typography noWrap sx={{ fontSize: '0.76rem', color: T.ink500 }}>{item.title} · {item.workerName}</Typography>
          </Box>
          <IconButton size="small" onClick={onClose} disabled={busy}><CloseRoundedIcon fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: '4px !important' }}>
        {/* The requester's reason, restated at the moment of the decision — the
            approver should never have to remember it from the panel behind. */}
        <Box sx={{ mb: 1.5, p: 1.25, borderRadius: '10px', bgcolor: '#F8FAFC', border: `1px solid ${T.line}` }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: T.ink400, mb: 0.5 }}>
            {t('inbox.reasonLabel')}
          </Typography>
          <Typography sx={{
            fontSize: '0.82rem', lineHeight: 1.5, whiteSpace: 'pre-wrap',
            color: item.description ? T.ink700 : TONE.warning.fg,
            fontWeight: item.description ? 400 : 600,
          }}>
            {item.description || t('inbox.noReason')}
          </Typography>
        </Box>

        <Typography sx={{ fontSize: '0.82rem', color: T.ink700, mb: 1.5 }}>{t(`inbox.modal.${mode}.body`)}</Typography>
        <TextField
          autoFocus fullWidth multiline minRows={3} size="small"
          label={t(isReject ? 'inbox.modal.reasonLabel' : 'inbox.modal.commentLabel')}
          required={reasonRequired}
          placeholder={t(isReject ? 'inbox.modal.reasonPlaceholder' : 'inbox.modal.commentPlaceholder')}
          value={text} onChange={(e) => setText(e.target.value)}
          error={reasonRequired && text.length > 0 && text.trim().length === 0}
        />
        <FormControlLabel
          sx={{ mt: 1, alignItems: 'flex-start', '& .MuiFormControlLabel-label': { fontSize: '0.8rem', color: T.ink700, mt: 0.5 } }}
          control={<Checkbox size="small" checked={confirmed} disabled={busy} onChange={(e) => setConfirmed(e.target.checked)} sx={{ color: accent.dot, '&.Mui-checked': { color: accent.dot } }} />}
          label={t('inbox.modal.confirmCheck')}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={busy} sx={{ color: T.ink500, fontWeight: 700 }}>{t('inbox.modal.cancel')}</Button>
        <Button
          variant="contained" disableElevation disabled={!canConfirm}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <Icon />}
          onClick={() => onConfirm(mode, { note: text.trim() })}
          sx={{ bgcolor: accent.dot, '&:hover': { bgcolor: isReject ? '#b91c1c' : isApprove ? '#327a8c' : accent.dot }, fontWeight: 700 }}
        >
          {t(`inbox.modal.${mode}.confirm`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ── Kanban board view ───────────────────────────────────────────────────────── */
const BOARD_COLS = [
  { key: 'for_approval', labelKey: 'inbox.board.forApproval', statuses: ['for_approval', 'pending'], tone: 'info' },
  { key: 'approved',     labelKey: 'inbox.board.approved',    statuses: ['approved'],                tone: 'success' },
  { key: 'rejected',     labelKey: 'inbox.board.rejected',    statuses: ['rejected'],                tone: 'error' },
];

function KanbanCard({ item, ctx, busy, onOpen, t }) {
  const accent = TYPE_BY_ID[item.type]?.color || T.brand;
  const def = TYPE_BY_ID[item.type];
  const canAct = !!def?.actions && ctx.isManager && (item.status === 'for_approval' || item.status === 'pending');
  const decided = item.status === 'approved' || item.status === 'rejected';
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(item.uid)}
      aria-label={`${item.title} — ${item.workerName}`}
      sx={{
        position: 'relative', width: '100%', textAlign: 'left', font: 'inherit',
        bgcolor: T.white, border: `1px solid ${T.line}`, borderRadius: '12px', p: 1.125, pl: 1.5,
        cursor: 'pointer', boxShadow: '0 1px 2px rgba(15,23,42,.04)',
        transition: 'border-color .16s, box-shadow .16s', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', left: 0, top: 8, bottom: 8, width: '3px', borderRadius: '2px', bgcolor: accent },
        '&:hover': { borderColor: accent, boxShadow: '0 3px 10px rgba(15,23,42,.08)' },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TypeIcon typeId={item.type} size={28} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: T.ink, lineHeight: 1.25, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.title}</Typography>
          <Stack direction="row" spacing={0.625} alignItems="center" sx={{ mt: 0.5, minWidth: 0 }}>
            <Avatar name={item.workerName} size={17} />
            <Typography noWrap sx={{ fontSize: '0.7rem', color: T.ink500, fontWeight: 500 }}>{item.workerName}</Typography>
          </Stack>
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" sx={{ mt: 0.875, gap: 0.5 }}>
        <Typography noWrap sx={{ fontSize: '0.66rem', color: T.ink400, minWidth: 0 }}>{t(`inbox.types.${item.type}`)}</Typography>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: '0.66rem', color: T.ink400, flexShrink: 0 }}>{relTime(item.createdAt || item.date)}</Typography>
      </Stack>

      {/* The requester's reason — visible on the card, because it is the basis
          of the decision the button below leads to. */}
      {item.description && (
        <Typography
          sx={{
            mt: 0.75, fontSize: '0.7rem', color: T.ink500, fontStyle: 'italic', lineHeight: 1.35,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}
        >
          “{item.description}”
        </Typography>
      )}

      {decided && item.decidedBy && (
        <Typography noWrap sx={{ mt: 0.5, fontSize: '0.64rem', color: T.ink400 }}>
          {t(`inbox.decidedBy.${item.status}`)}: <b style={{ color: T.ink700 }}>{item.decidedBy}</b>
        </Typography>
      )}

      {/* No inline approve. Deciding requires the detail view, where the reason,
          the approval chain and the history are all in front of the approver. */}
      {canAct && (
        <Box
          component="span"
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.375, mt: 1,
            px: 0.875, py: 0.375, borderRadius: '6px',
            bgcolor: TONE.info.bg, color: TONE.info.fg,
            fontSize: '0.68rem', fontWeight: 700,
            opacity: busy ? 0.5 : 1,
          }}
        >
          <CheckRoundedIcon sx={{ fontSize: 13 }} />
          {t('inbox.openToDecide')}
        </Box>
      )}
    </Box>
  );
}

function KanbanBoard({ items, ctx, busy, onOpen, t }) {
  const byCol = useMemo(() => {
    const m = { for_approval: [], approved: [], rejected: [] };
    items.forEach((it) => { const st = it.status === 'pending' ? 'for_approval' : it.status; if (m[st]) m[st].push(it); });
    return m;
  }, [items]);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5, alignItems: 'start' }}>
      {BOARD_COLS.map((col) => {
        const list = byCol[col.key] || [];
        const tone = TONE[col.tone];
        return (
          <Box key={col.key} sx={{ bgcolor: T.surface, border: `1px solid ${T.line}`, borderRadius: '14px', display: 'flex', flexDirection: 'column', minHeight: 140 }}>
            <Box sx={{ px: 1.5, py: 1.125, display: 'flex', alignItems: 'center', gap: 0.75, borderBottom: `1px solid ${T.lineSoft}` }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone.dot }} />
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: tone.fg }}>{t(col.labelKey)}</Typography>
              <Box sx={{ ml: 'auto', minWidth: 20, height: 20, px: 0.5, borderRadius: '999px', bgcolor: tone.bg, color: tone.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.66rem', fontWeight: 800 }}>{list.length}</Box>
            </Box>
            <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1, maxHeight: { xs: 'none', sm: 'calc(100vh - 320px)' }, overflowY: 'auto' }}>
              {list.length === 0
                ? <Typography sx={{ textAlign: 'center', color: T.ink400, fontSize: '0.76rem', py: 3 }}>{t('inbox.board.empty')}</Typography>
                : list.map((it) => <KanbanCard key={it.uid} item={it} ctx={ctx} busy={busy} onOpen={onOpen} t={t} />)}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

/* ── Compact table view ──────────────────────────────────────────────────────── */
const metaSummary = (item) => (item.meta || []).map((m) => m.value).filter(Boolean).join(' · ');

const HEAD_SX = { fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: T.ink400, borderBottom: `1px solid ${T.line}`, py: 0.875, bgcolor: T.surface, whiteSpace: 'nowrap' };
const CELL_SX = { borderBottom: `1px solid ${T.lineSoft}`, py: 0.75 };

function RequestTable({ rows, loading, ctx, busy, page, rpp, onPage, onRpp, sortBy, sortDir, onSort, onOpen, t }) {
  const start = page * rpp;
  const paged = rows.slice(start, start + rpp);

  const SortCell = ({ col, children, sx }) => (
    <TableCell sx={sx} sortDirection={sortBy === col ? sortDir : false}>
      <TableSortLabel
        active={sortBy === col} direction={sortBy === col ? sortDir : 'asc'}
        onClick={() => onSort(col)}
        sx={{ '& .MuiTableSortLabel-icon': { fontSize: 15 } }}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Paper variant="outlined" sx={{ borderRadius: '14px', overflow: 'hidden', border: `1px solid ${T.line}` }}>
      <TableContainer sx={{ maxWidth: '100%' }}>
        <Table size="small" sx={{ '& td, & th': { borderColor: T.lineSoft } }}>
          <TableHead>
            <TableRow>
              <SortCell col="title" sx={{ ...HEAD_SX, pl: 2 }}>{t('inbox.table.request', 'Kërkesa')}</SortCell>
              <SortCell col="worker" sx={{ ...HEAD_SX, display: { xs: 'none', md: 'table-cell' } }}>{t('inbox.table.worker', 'Punonjësi')}</SortCell>
              <TableCell sx={{ ...HEAD_SX, display: { xs: 'none', lg: 'table-cell' } }}>{t('inbox.table.details', 'Detaje')}</TableCell>
              <SortCell col="created" sx={{ ...HEAD_SX, display: { xs: 'none', sm: 'table-cell' } }}>{t('inbox.table.submitted', 'Dorëzuar')}</SortCell>
              <SortCell col="status" sx={HEAD_SX}>{t('inbox.table.status', 'Statusi')}</SortCell>
              <TableCell sx={{ ...HEAD_SX, pr: 1.5 }} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && Array.from({ length: 6 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={6} sx={{ ...CELL_SX, py: 1 }}><Skeleton variant="rounded" height={30} /></TableCell>
              </TableRow>
            ))}

            {!loading && paged.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} sx={{ ...CELL_SX, textAlign: 'center', py: 6, color: T.ink400 }}>
                  <CheckCircleOutlineRoundedIcon sx={{ fontSize: 32, color: T.ink300, mb: 0.5, display: 'block', mx: 'auto' }} />
                  <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: T.ink700 }}>{t('inbox.empty.title')}</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: T.ink500 }}>{t('inbox.empty.sub')}</Typography>
                </TableCell>
              </TableRow>
            )}

            {!loading && paged.map((item) => {
              const accent = TYPE_BY_ID[item.type]?.color || T.brand;
              const def = TYPE_BY_ID[item.type];
              const canAct = !!def?.actions && ctx.isManager && (item.status === 'for_approval' || item.status === 'pending');
              const summary = metaSummary(item);
              return (
                <TableRow
                  key={item.uid} hover onClick={() => onOpen(item.uid)}
                  sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 }, '&:hover': { bgcolor: '#F6FAFB' } }}
                >
                  {/* Request: icon + title (+ ref) */}
                  <TableCell sx={{ ...CELL_SX, pl: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <Box sx={{ width: 3, height: 26, borderRadius: '2px', bgcolor: accent, flexShrink: 0, opacity: 0.7 }} />
                      <TypeIcon typeId={item.type} size={30} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 700, color: T.ink }}>{item.title}</Typography>
                        <Typography noWrap sx={{ fontSize: '0.68rem', color: T.ink500, display: { md: 'none' } }}>{item.workerName}</Typography>
                        {item.refNumber && <Typography noWrap sx={{ fontSize: '0.64rem', color: T.ink400, fontFamily: 'monospace', display: { xs: 'none', md: 'block' } }}>{item.refNumber}</Typography>}
                      </Box>
                    </Stack>
                  </TableCell>

                  {/* Worker */}
                  <TableCell sx={{ ...CELL_SX, display: { xs: 'none', md: 'table-cell' } }}>
                    <Stack direction="row" spacing={0.875} alignItems="center" sx={{ minWidth: 0 }}>
                      <Avatar name={item.workerName} size={24} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: 600, color: T.ink700 }}>{item.workerName}</Typography>
                        <Typography noWrap sx={{ fontSize: '0.66rem', color: T.ink400 }}>{t(`inbox.types.${item.type}`)}</Typography>
                      </Box>
                    </Stack>
                  </TableCell>

                  {/* Details — meta plus the requester's reason. The reason is
                      what a decision turns on, so it is shown in the list, not
                      hidden one click away. */}
                  <TableCell sx={{ ...CELL_SX, display: { xs: 'none', lg: 'table-cell' }, maxWidth: 260 }}>
                    <Typography noWrap sx={{ fontSize: '0.76rem', color: T.ink700 }}>{summary || '—'}</Typography>
                    <Tooltip title={item.description || ''} disableHoverListener={!item.description}>
                      <Typography
                        noWrap
                        sx={{ fontSize: '0.68rem', color: item.description ? T.ink500 : T.ink400, fontStyle: item.description ? 'italic' : 'normal' }}
                      >
                        {item.description ? `“${item.description}”` : t('inbox.noReason')}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  {/* Submitted */}
                  <TableCell sx={{ ...CELL_SX, display: { xs: 'none', sm: 'table-cell' }, whiteSpace: 'nowrap' }}>
                    <Tooltip title={dateTime(item.createdAt || item.date)}>
                      <Typography sx={{ fontSize: '0.74rem', color: T.ink500 }}>{relTime(item.createdAt || item.date)}</Typography>
                    </Tooltip>
                  </TableCell>

                  {/* Status */}
                  <TableCell sx={CELL_SX}>
                    <StatusPill status={item.status} label={t(`inbox.status.${item.status}`)} />
                  </TableCell>

                  {/* Actions — a decision is never taken from the row. Approving
                      from here would mean approving without having read the
                      requester's reason, so both buttons open the detail where
                      the reason and history are in view. */}
                  <TableCell sx={{ ...CELL_SX, pr: 1.5 }} align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                      {canAct && (
                        <Button
                          size="small" variant="outlined" disabled={busy}
                          onClick={(e) => { e.stopPropagation(); onOpen(item.uid); }}
                          sx={{ py: 0.25, px: 1, fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap', borderColor: T.line, color: T.brand, '&:hover': { borderColor: T.brand, bgcolor: '#F2FAFC' } }}
                        >
                          {t('inbox.openToDecide')}
                        </Button>
                      )}
                      <ChevronRightRoundedIcon sx={{ fontSize: 18, color: T.ink300 }} />
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && rows.length > 0 && (
        <TablePagination
          component="div" count={rows.length} page={page} rowsPerPage={rpp}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, p) => onPage(p)} onRowsPerPageChange={(e) => onRpp(parseInt(e.target.value, 10))}
          labelRowsPerPage={t('inbox.table.perPage', 'Për faqe')}
          sx={{ borderTop: `1px solid ${T.lineSoft}`, '& .MuiTablePagination-toolbar': { minHeight: 44 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.74rem', color: T.ink500 } }}
        />
      )}
    </Paper>
  );
}

/* ── Professional report (tabular) ───────────────────────────────────────────── */
const REPORT_STATUS = [
  { key: 'for_approval', label: 'Për aprovim', tone: 'info' },
  { key: 'approved',     label: 'Aprovuara',   tone: 'success' },
  { key: 'rejected',     label: 'Refuzuara',   tone: 'error' },
];
const pctStr = (c, tot) => `${tot > 0 ? Math.round((c / tot) * 100) : 0}%`;
const R_HEAD = { fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: T.ink400, bgcolor: T.surface, borderBottom: `1px solid ${T.line}`, py: 0.75, whiteSpace: 'nowrap' };
const R_CELL = { fontSize: '0.8rem', borderBottom: `1px solid ${T.lineSoft}`, py: 0.625 };

/* A titled, bordered data table used across the report */
function ReportTable({ title, head, rows, aligns = [], maxHeight }) {
  return (
    <Box>
      {title && <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', color: T.ink500, mb: 0.625 }}>{title}</Typography>}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px', borderColor: T.line, maxHeight }}>
        <Table size="small" stickyHeader={!!maxHeight}>
          <TableHead>
            <TableRow>{head.map((h, i) => <TableCell key={i} align={aligns[i] || 'left'} sx={{ ...R_HEAD, pl: i === 0 ? 1.5 : 1 }}>{h}</TableCell>)}</TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, ri) => (
              <TableRow key={ri} hover>
                {r.map((c, ci) => (
                  <TableCell key={ci} align={aligns[ci] || 'left'} sx={{ ...R_CELL, pl: ci === 0 ? 1.5 : 1, fontWeight: ci === 0 ? 600 : 700, color: ci === 0 ? T.ink700 : T.ink }}>{c}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

const dOnlyStr = (v) => (v ? String(v).slice(0, 10) : '');
const fmtDMY = (d) => (d ? d.split('-').reverse().join('.') : '…');
const statusText = (t, s) => t(`inbox.status.${s === 'pending' ? 'for_approval' : s}`);
const decidedName = (it) => ((it.status === 'approved' || it.status === 'rejected') ? (it.decidedBy || '—') : '—');

// xlsx-js-style helpers
const BRAND_RGB = '3D8FA3';
const xBorder = (rgb) => ({ top: { style: 'thin', color: { rgb } }, bottom: { style: 'thin', color: { rgb } }, left: { style: 'thin', color: { rgb } }, right: { style: 'thin', color: { rgb } } });
const X = {
  title:   { font: { bold: true, sz: 14, color: { rgb: 'FFFFFF' } }, fill: { patternType: 'solid', fgColor: { rgb: BRAND_RGB } }, alignment: { horizontal: 'left', vertical: 'center' } },
  sub:     { font: { italic: true, sz: 9, color: { rgb: '64748B' } } },
  section: { font: { bold: true, sz: 10, color: { rgb: '0F172A' } }, fill: { patternType: 'solid', fgColor: { rgb: 'EEF2F6' } } },
  head:    { font: { bold: true, sz: 9, color: { rgb: 'FFFFFF' } }, fill: { patternType: 'solid', fgColor: { rgb: BRAND_RGB } }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: xBorder('B8C6CE') },
  kpi:     { font: { bold: true, sz: 12, color: { rgb: '0F172A' } }, alignment: { horizontal: 'center', vertical: 'center' }, border: xBorder('E2E8F0') },
  cell:    { font: { sz: 9, color: { rgb: '334155' } }, alignment: { vertical: 'center', wrapText: true }, border: xBorder('E2E8F0') },
  cellAlt: { font: { sz: 9, color: { rgb: '334155' } }, alignment: { vertical: 'center', wrapText: true }, border: xBorder('E2E8F0'), fill: { patternType: 'solid', fgColor: { rgb: 'F8FAFC' } } },
};
const setCell = (ws, r, c, style) => { const a = XLSX.utils.encode_cell({ r, c }); if (ws[a]) ws[a].s = style; };

function ApprovalReport({ open, onClose, data, typeLabel, initialFrom, initialTo, t }) {
  const [from, setFrom] = useState(initialFrom || '');
  const [to, setTo] = useState(initialTo || '');
  // Re-sync to the page's date range each time the report is opened.
  useEffect(() => { if (open) { setFrom(initialFrom || ''); setTo(initialTo || ''); } }, [open, initialFrom, initialTo]);

  const rows = useMemo(() => data.filter((it) => {
    const d = dOnlyStr(it.createdAt || it.date);
    if (from && !(d && d >= from)) return false;
    if (to && !(d && d <= to)) return false;
    return true;
  }), [data, from, to]);

  const stats = useMemo(() => {
    const norm = (s) => (s === 'pending' ? 'for_approval' : s);
    const byStatus = { for_approval: 0, approved: 0, rejected: 0 };
    const byType = {};
    rows.forEach((it) => {
      const st = norm(it.status); if (byStatus[st] != null) byStatus[st] += 1;
      byType[it.type] = (byType[it.type] || 0) + 1;
    });
    const total = rows.length;
    const decided = byStatus.approved + byStatus.rejected;
    const rate = decided > 0 ? Math.round((byStatus.approved / decided) * 100) : 0;
    const types = Object.entries(byType).map(([id, count]) => ({ id, label: t(`inbox.types.${id}`), color: TYPE_BY_ID[id]?.color || T.brand, count })).sort((a, b) => b.count - a.count);
    return { total, byStatus, decided, rate, types };
  }, [rows, t]);

  const periodLabel = `${typeLabel}  ·  ${(from || to) ? `${fmtDMY(from)} – ${fmtDMY(to)}` : 'Të gjitha datat'}`;
  const genStamp = new Date().toLocaleString('sq-AL');

  // Detail rows — now include "Vendosi" (decided by) and "Komenti" (decision note).
  const DETAIL_HEAD = ['Kërkesa', 'Punonjësi', 'Lloji', 'Dorëzuar', 'Statusi', 'Vendosi', 'Komenti'];
  const detailRows = () => rows.map((it) => [
    it.title || '', it.workerName || '', t(`inbox.types.${it.type}`),
    dateTime(it.createdAt || it.date), statusText(t, it.status),
    decidedName(it), it.decisionNote || '',
  ]);
  // Breakdown bodies shared by the on-screen report and both exporters.
  const typeBody = () => stats.types.map((x) => [x.label, x.count, pctStr(x.count, stats.total)]);
  const statusBody = () => REPORT_STATUS.map((s) => [s.label, stats.byStatus[s.key], pctStr(stats.byStatus[s.key], stats.total)]);
  const stamp = () => ymd(new Date());

  // Single-sheet Excel: the whole report (summary + by type + by status + detail)
  // stacked on one sheet — no second tab.
  const exportExcel = () => {
    const NC = DETAIL_HEAD.length; // 7 columns wide
    const tBody = typeBody();
    const sBody = statusBody();
    const detail = detailRows();
    const pad = (row, n = NC) => { const r = row.slice(); while (r.length < n) r.push(''); return r; };

    const aoa = [];
    const merges = [];
    const styleOps = []; // { r, c, s }
    const push = (row) => { aoa.push(row); return aoa.length - 1; };
    const styleRow = (r, cols, s) => cols.forEach((c) => styleOps.push({ r, c, s }));
    const sectionRow = (label) => { const r = push([label]); merges.push({ s: { r, c: 0 }, e: { r, c: NC - 1 } }); styleRow(r, [0], X.section); return r; };

    // Title block
    const titleR = push(['Kërkesat dhe Aprovimet — Raport']);
    const subR1 = push([`Periudha: ${periodLabel}`]);
    const subR2 = push([`Gjeneruar: ${genStamp}`]);
    [titleR, subR1, subR2].forEach((r) => merges.push({ s: { r, c: 0 }, e: { r, c: NC - 1 } }));
    styleRow(titleR, [0], X.title); styleRow(subR1, [0], X.sub); styleRow(subR2, [0], X.sub);
    push([]);

    // Përmbledhje — horizontal KPI strip
    sectionRow('PËRMBLEDHJE');
    const kpiHeadR = push(pad(['Gjithsej', 'Për aprovim', 'Aprovuara', 'Refuzuara', 'Shkalla e aprovimit']));
    const kpiValR = push(pad([stats.total, stats.byStatus.for_approval, stats.byStatus.approved, stats.byStatus.rejected, `${stats.rate}%`]));
    styleRow(kpiHeadR, [0, 1, 2, 3, 4], X.head);
    styleRow(kpiValR, [0, 1, 2, 3, 4], X.kpi);
    push([]);

    // Sipas llojit
    sectionRow('SIPAS LLOJIT');
    const tHeadR = push(pad(['Lloji', 'Numri', '%']));
    styleRow(tHeadR, [0, 1, 2], X.head);
    tBody.forEach((row, i) => { const r = push(pad(row)); styleRow(r, [0, 1, 2], i % 2 ? X.cellAlt : X.cell); });
    push([]);

    // Sipas statusit
    sectionRow('SIPAS STATUSIT');
    const sHeadR = push(pad(['Statusi', 'Numri', '%']));
    styleRow(sHeadR, [0, 1, 2], X.head);
    sBody.forEach((row, i) => { const r = push(pad(row)); styleRow(r, [0, 1, 2], i % 2 ? X.cellAlt : X.cell); });
    push([]);

    // Detajet
    sectionRow(`DETAJET E KËRKESAVE (${rows.length})`);
    const dHeadR = push(DETAIL_HEAD.slice());
    styleRow(dHeadR, DETAIL_HEAD.map((_, c) => c), X.head);
    detail.forEach((row, i) => { const r = push(pad(row)); styleRow(r, DETAIL_HEAD.map((_, c) => c), i % 2 ? X.cellAlt : X.cell); });

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [34, 22, 20, 20, 18, 22, 40].map((wch) => ({ wch }));
    ws['!merges'] = merges;
    ws['!rows'] = [{ hpt: 22 }];
    styleOps.forEach(({ r, c, s }) => setCell(ws, r, c, s));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Raporti');
    XLSX.writeFile(wb, `kerkesat-aprovimet-${stamp()}.xlsx`);
  };

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const brand = [61, 143, 163];
    const drawHeader = () => {
      doc.setFillColor(...brand); doc.rect(0, 0, W, 17, 'F');
      doc.setTextColor(255); doc.setFont('helvetica', 'bold'); doc.setFontSize(14);
      doc.text('Kërkesat dhe Aprovimet', 14, 11);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
      doc.text(periodLabel, W - 14, 7, { align: 'right' });
      doc.text(`Gjeneruar: ${genStamp}`, W - 14, 12, { align: 'right' });
    };
    const drawFooter = () => {
      doc.setFontSize(8); doc.setTextColor(150);
      doc.text('Nexuma HR', 14, H - 6);
      doc.text(`Faqe ${doc.internal.getNumberOfPages()}`, W - 14, H - 6, { align: 'right' });
    };
    const decorate = () => { drawHeader(); drawFooter(); };
    const sectionTitle = (txt, x, y) => { doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(71, 85, 105); doc.text(txt, x, y); };
    const breakdownStyles = {
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 1.6, lineColor: [226, 232, 240] },
      headStyles: { fillColor: [241, 245, 249], textColor: [51, 65, 85], fontStyle: 'bold' },
      columnStyles: { 1: { halign: 'right', cellWidth: 18 }, 2: { halign: 'right', cellWidth: 16 } },
    };

    // Summary strip
    autoTable(doc, {
      startY: 23, margin: { left: 14, right: 14 },
      head: [['Gjithsej', 'Për aprovim', 'Aprovuara', 'Refuzuara', 'Shkalla e aprovimit']],
      body: [[stats.total, stats.byStatus.for_approval, stats.byStatus.approved, stats.byStatus.rejected, `${stats.rate}%`]],
      theme: 'grid',
      styles: { fontSize: 9, halign: 'center', cellPadding: 2, lineColor: [226, 232, 240] },
      headStyles: { fillColor: [241, 245, 249], textColor: [51, 65, 85], fontStyle: 'bold', halign: 'center' },
      bodyStyles: { fontSize: 12, fontStyle: 'bold', textColor: [15, 23, 42] },
      margin: { top: 21, left: 14, right: 14 },
      didDrawPage: decorate,
    });

    // Breakdown — Sipas llojit | Sipas statusit (side by side)
    const gap = 8;
    const half = (W - 28 - gap) / 2;
    let y = doc.lastAutoTable.finalY + 10;
    sectionTitle('Sipas llojit', 14, y - 3);
    sectionTitle('Sipas statusit', 14 + half + gap, y - 3);
    autoTable(doc, { ...breakdownStyles, startY: y, margin: { left: 14, top: 21 }, tableWidth: half, head: [['Lloji', 'Numri', '%']], body: typeBody() });
    const yLeft = doc.lastAutoTable.finalY;
    autoTable(doc, { ...breakdownStyles, startY: y, margin: { left: 14 + half + gap, top: 21 }, tableWidth: half, head: [['Statusi', 'Numri', '%']], body: statusBody() });
    y = Math.max(yLeft, doc.lastAutoTable.finalY) + 10;

    // Detail table
    sectionTitle(`Detajet e kërkesave (${rows.length})`, 14, y - 3);
    autoTable(doc, {
      startY: y, margin: { top: 21, left: 14, right: 14, bottom: 12 },
      head: [DETAIL_HEAD], body: detailRows(),
      theme: 'striped',
      styles: { fontSize: 7.5, cellPadding: 1.6, overflow: 'linebreak', lineColor: [232, 236, 241], valign: 'middle' },
      headStyles: { fillColor: brand, textColor: 255, fontStyle: 'bold', fontSize: 8 },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: { 0: { cellWidth: 44 }, 1: { cellWidth: 36 }, 2: { cellWidth: 28 }, 3: { cellWidth: 32 }, 4: { cellWidth: 24 }, 5: { cellWidth: 32 }, 6: { cellWidth: 'auto' } },
      didDrawPage: decorate,
    });

    doc.save(`kerkesat-aprovimet-${stamp()}.pdf`);
  };

  const summaryHead = ['Gjithsej', 'Për aprovim', 'Aprovuara', 'Refuzuara', 'Shkalla'];
  const summaryRow = [[stats.total, stats.byStatus.for_approval, stats.byStatus.approved, stats.byStatus.rejected, `${stats.rate}%`]];
  const dot = (color) => <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}><Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} /></Box>;
  const typeRows = stats.types.map((x) => [<Stack direction="row" spacing={0.75} alignItems="center">{dot(x.color)}<span>{x.label}</span></Stack>, x.count, pctStr(x.count, stats.total)]);
  const statusRows = REPORT_STATUS.map((s) => [<Stack direction="row" spacing={0.75} alignItems="center">{dot(TONE[s.tone].dot)}<span>{s.label}</span></Stack>, stats.byStatus[s.key], pctStr(stats.byStatus[s.key], stats.total)]);
  const dateSx = { width: 150, '& .MuiOutlinedInput-root': { borderRadius: '9px' } };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${T.lineSoft}`, display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
        <Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: `${T.brand}14`, color: T.brand, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <InsertChartRoundedIcon />
        </Box>
        <Box sx={{ flex: 1, minWidth: 140 }}>
          <Typography sx={{ fontSize: '1.02rem', fontWeight: 800, color: T.ink }}>Raporti i kërkesave</Typography>
          <Typography noWrap sx={{ fontSize: '0.76rem', color: T.ink500 }}>{periodLabel}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseRoundedIcon fontSize="small" /></IconButton>
      </Box>

      {/* Toolbar: date filters + exports */}
      <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${T.lineSoft}`, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', bgcolor: T.white }}>
        <TextField size="small" type="date" label="Nga" InputLabelProps={{ shrink: true }} value={from}
          onChange={(e) => setFrom(e.target.value)} inputProps={{ max: to || undefined }} sx={dateSx} />
        <TextField size="small" type="date" label="Deri" InputLabelProps={{ shrink: true }} value={to}
          onChange={(e) => setTo(e.target.value)} inputProps={{ min: from || undefined }} sx={dateSx} />
        {(from || to) && (
          <Button size="small" onClick={() => { setFrom(''); setTo(''); }} startIcon={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{ color: T.ink500, textTransform: 'none', fontWeight: 600 }}>Pastro datat</Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: T.ink500, mr: 0.5 }}>{stats.total} rezultate</Typography>
        <Button size="small" variant="outlined" startIcon={<GridOnRoundedIcon />} disabled={stats.total === 0} onClick={exportExcel}
          sx={{ color: '#15803D', borderColor: '#BBF7D0', textTransform: 'none', fontWeight: 700, '&:hover': { borderColor: '#15803D', bgcolor: '#F0FDF4' } }}>Excel</Button>
        <Button size="small" variant="outlined" startIcon={<PictureAsPdfRoundedIcon />} disabled={stats.total === 0} onClick={exportPDF}
          sx={{ color: '#B91C1C', borderColor: '#FECACA', textTransform: 'none', fontWeight: 700, '&:hover': { borderColor: '#B91C1C', bgcolor: '#FEF2F2' } }}>PDF</Button>
      </Box>

      <DialogContent sx={{ p: 2, bgcolor: T.surface }}>
        {stats.total === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center', color: T.ink400 }}>
            <InsertChartRoundedIcon sx={{ fontSize: 34, color: T.ink300, mb: 0.5 }} />
            <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: T.ink700 }}>Asnjë kërkesë për këtë periudhë/filtër</Typography>
          </Box>
        ) : (
          <Stack spacing={1.75}>
            {/* Summary table */}
            <ReportTable title="Përmbledhje" head={summaryHead} rows={summaryRow} aligns={['right', 'right', 'right', 'right', 'right']} />

            {/* By type + by status */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
              <ReportTable title="Sipas llojit" head={['Lloji', 'Numri', '%']} rows={typeRows} aligns={['left', 'right', 'right']} />
              <ReportTable title="Sipas statusit" head={['Statusi', 'Numri', '%']} rows={statusRows} aligns={['left', 'right', 'right']} />
            </Box>

            {/* Detail table */}
            <ReportTable title={`Detajet e kërkesave (${rows.length})`} head={DETAIL_HEAD} rows={detailRows()} maxHeight={340} />
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── Main ────────────────────────────────────────────────────────────────────── */
export default function ApprovalInbox() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const ctx = useMemo(getInboxContext, []);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('for_approval');
  const [search, setSearch] = useState('');
  const [selectedUid, setSelectedUid] = useState(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: '', sev: 'success' });
  const [createAnchor, setCreateAnchor] = useState(null);
  const [createType, setCreateType] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(25);
  const [filterType, setFilterType] = useState('all');
  const [filterFrom, setFilterFrom] = useState(() => currentYearRange().from);
  const [filterTo, setFilterTo] = useState(() => currentYearRange().to);
  const [sortBy, setSortBy] = useState('created');   // 'title' | 'worker' | 'created' | 'status'
  const [sortDir, setSortDir] = useState('desc');    // 'asc' | 'desc'
  const [reportOpen, setReportOpen] = useState(false);
  const [view, setView] = useState('table');         // 'table' | 'kanban'
  const [category, setCategory] = useState('all');   // 'all' | 'leave' | 'other'

  // Request types the user can create in-place (have a `create` descriptor wired).
  // `create.when` filtron edhe menunë, jo vetëm fushat: një lloj kërkese që
  // përdoruesi s'mund ta hapë nuk duhet të rrijë në listë dhe ta kthejë 403-shin
  // vetëm pasi ta ketë plotësuar formularin.
  const creatableTypes = useMemo(
    () => TYPES.filter((ty) => ty.enabled && ty.create && !ty.hideFromCreateMenu
      && (typeof ty.create.when === 'function' ? ty.create.when(ctx) : true)),
    [ctx],
  );

  const load = useCallback(async () => {
    setLoading(true);
    // Një lexim, dy përdorime: harta etiketon kërkesat e ardhura, lista ushqen
    // zgjedhësit. Lista mban edhe gjininë — pa të, zgjedhësi i lehonisë s'do ta
    // dinte kë të nxjerrë.
    const { map: workersMap, list: workerList } = await loadWorkerDirectory();
    setWorkers(workerList);
    const data = await fetchAllRequests({ ...ctx, workersMap });
    setItems(data);
    setLoading(false);
  }, [ctx]);

  useEffect(() => { load(); }, [load]);

  // Deep-link: /inbox?create=<typeId> opens the create dialog for that type
  // (e.g. an employee clicking "Kërko korrigjim" from their monthly schedule
  // lands straight on the attendance-correction form). The param is stripped so
  // a refresh or back-navigation doesn't re-open it.
  useEffect(() => {
    const want = new URLSearchParams(location.search).get('create');
    if (!want) return;
    const ty = TYPES.find((x) => x.id === want && x.enabled && x.create);
    if (ty) setCreateType(ty);
    history.replace('/inbox');
  }, [location.search, history]);

  // Everything in the active category — the base every other count and filter
  // narrows from, so the status tabs never advertise rows the category hides.
  const inCategory = useMemo(
    () => (category === 'all' ? items : items.filter((it) => categoryOf(it.type) === category)),
    [items, category],
  );

  const counts = useMemo(() => {
    const c = { all: inCategory.length, for_approval: 0, approved: 0, rejected: 0 };
    inCategory.forEach((it) => {
      const st = (it.status === 'pending') ? 'for_approval' : it.status; // pending folds into the action queue
      if (c[st] != null) c[st] += 1;
    });
    return c;
  }, [inCategory]);

  // Per-category totals for the segmented control's badges.
  const categoryCounts = useMemo(() => {
    const c = { all: items.length, leave: 0, other: 0 };
    items.forEach((it) => { c[categoryOf(it.type)] += 1; });
    return c;
  }, [items]);

  // Filter options derived from the requests actually present, so a filter never
  // lists a type/worker with zero matches.
  const typeOptions = useMemo(() => {
    const ids = [...new Set(inCategory.map((it) => it.type))];
    return ids.map((id) => ({ id, label: t(`inbox.types.${id}`), color: TYPE_BY_ID[id]?.color || T.brand }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [inCategory, t]);

  // Everything matching the category/type/date/search filters, across ALL
  // statuses. The table narrows this by the active tab; the Kanban splits it
  // into columns.
  const boardItems = useMemo(() => {
    let list = inCategory;
    if (filterType !== 'all') list = list.filter((it) => it.type === filterType);
    if (filterFrom) list = list.filter((it) => { const d = (it.createdAt || it.date || '').slice(0, 10); return d && d >= filterFrom; });
    if (filterTo)   list = list.filter((it) => { const d = (it.createdAt || it.date || '').slice(0, 10); return d && d <= filterTo; });
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((it) =>
      [it.title, it.workerName, it.description, it.refNumber].filter(Boolean).some((s) => String(s).toLowerCase().includes(q)));
    return list;
  }, [inCategory, search, filterType, filterFrom, filterTo]);

  const visible = useMemo(() => {
    const allow = TAB_STATUSES[tab];
    const list = !allow ? boardItems : boardItems.filter((it) => allow.includes(it.status));
    const dir = sortDir === 'asc' ? 1 : -1;
    const key = (it) => {
      if (sortBy === 'title') return String(it.title || '').toLowerCase();
      if (sortBy === 'worker') return String(it.workerName || '').toLowerCase();
      if (sortBy === 'status') return String(it.status || '');
      return new Date(it.createdAt || it.date || 0).getTime(); // 'created'
    };
    return [...list].sort((a, b) => { const ka = key(a), kb = key(b); return ka < kb ? -dir : ka > kb ? dir : 0; });
  }, [boardItems, tab, sortBy, sortDir]);

  // Defaults the filter bar returns to (current year). "Pastro" appears only when
  // something differs from these, and resets back to them.
  const defRange = useMemo(currentYearRange, []);
  const filtersActive = filterType !== 'all' || search.trim().length > 0 || filterFrom !== defRange.from || filterTo !== defRange.to;
  const clearFilters = () => { setFilterType('all'); setFilterFrom(defRange.from); setFilterTo(defRange.to); setSearch(''); setPage(0); };
  const onSort = (col) => {
    if (sortBy === col) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); return; }
    setSortBy(col);
    setSortDir(col === 'created' ? 'desc' : 'asc'); // dates default newest-first, text A→Z
  };

  // Report dataset: honours only the TYPE filter (all statuses, ignores tab/search).
  // The report has its own date range so it stays self-contained.
  const reportData = useMemo(() => {
    if (filterType === 'all') return inCategory;
    return inCategory.filter((it) => it.type === filterType);
  }, [inCategory, filterType]);
  const reportTypeLabel = filterType !== 'all'
    ? (typeOptions.find((o) => o.id === filterType)?.label || '')
    : t('inbox.filter.allTypes', 'Të gjitha llojet');

  const selected = useMemo(() => items.find((it) => it.uid === selectedUid) || null, [items, selectedUid]);

  // Table is the primary view; the detail opens on demand in a dialog. On small
  // screens the dialog goes full-screen (isWide drives that below).
  const isWide = useMediaQuery('(min-width:900px)');

  const runAction = async (action, { note } = {}, itemArg) => {
    const target = itemArg || selected;
    if (!target) return;
    const def = TYPE_BY_ID[target.type];
    const fn = def?.actions?.[action];
    if (!fn) return;
    setBusy(true);
    const r = await fn(target, { note });
    setBusy(false);
    if (!r?.ok) { setToast({ open: true, msg: r?.error || t('inbox.toast.error'), sev: 'error' }); return; }
    setToast({ open: true, msg: t(`inbox.toast.${action}`), sev: action === 'reject' ? 'info' : 'success' });
    await load();
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, width: '100%' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1.25} alignItems="center">
          <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: `${T.brand}14`, color: T.brand, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <MoveToInboxRoundedIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: { xs: '1.05rem', md: '1.2rem' }, fontWeight: 800, color: T.ink, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
              {t('inbox.title')}
            </Typography>
            <Typography sx={{ fontSize: '0.76rem', color: T.ink500 }}>{t('inbox.subtitle')}</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          {creatableTypes.length > 0 && (
            <>
              <Button
                variant="contained" disableElevation startIcon={<AddRoundedIcon />}
                onClick={(e) => setCreateAnchor(e.currentTarget)}
                sx={{ bgcolor: T.brand, fontWeight: 700, borderRadius: '10px', textTransform: 'none', px: 1.75, '&:hover': { bgcolor: '#327C8E' } }}
              >
                {t('inbox.newRequest')}
              </Button>
              <Menu
                anchorEl={createAnchor} open={!!createAnchor} onClose={() => setCreateAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { mt: 0.75, borderRadius: '12px', minWidth: 230, boxShadow: '0 8px 28px rgba(15,23,42,.12)' } }}
              >
                <Typography sx={{ px: 1.5, py: 0.75, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: T.ink400 }}>
                  {t('inbox.newRequestPick')}
                </Typography>
                {creatableTypes.map((ty) => (
                  <MenuItem
                    key={ty.id}
                    onClick={() => { setCreateAnchor(null); setCreateType(ty); }}
                    sx={{ py: 1, gap: 0.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 0 }}>
                      <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: `${ty.color}14`, color: ty.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ty.Icon sx={{ fontSize: 17 }} />
                      </Box>
                    </ListItemIcon>
                    <Typography sx={{ fontSize: '0.84rem', fontWeight: 600, color: T.ink }}>{t(`inbox.types.${ty.id}`)}</Typography>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          <Tooltip title={t('inbox.refresh')}>
            <IconButton onClick={load} sx={{ border: `1px solid ${T.line}`, borderRadius: '10px', bgcolor: T.white, '&:hover': { borderColor: T.brand, color: T.brand } }}><RefreshRoundedIcon /></IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Category — the primary split the Approval Center is organised around:
          leave decisions vs everything else. A segmented radiogroup, so arrow
          keys and screen readers work as they would on native radios. */}
      <Box
        role="radiogroup"
        aria-label={t('inbox.category.all')}
        sx={{
          display: 'inline-flex', mb: 1.5, p: 0.375, gap: 0.375,
          border: `1px solid ${T.line}`, borderRadius: '10px', bgcolor: T.surface,
          maxWidth: '100%', overflowX: 'auto',
        }}
      >
        {CATEGORIES.map((c) => {
          const on = category === c;
          return (
            <Box
              key={c}
              component="button"
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => { setCategory(c); setFilterType('all'); setPage(0); }}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.625,
                px: 1.25, py: 0.625, borderRadius: '8px', border: 'none',
                cursor: 'pointer', whiteSpace: 'nowrap', font: 'inherit',
                fontSize: '0.78rem', fontWeight: on ? 800 : 600,
                bgcolor: on ? T.white : 'transparent',
                color: on ? T.brand : T.ink500,
                boxShadow: on ? '0 1px 2px rgba(15,23,42,.08)' : 'none',
                transition: 'background-color .16s, color .16s',
                '&:hover': on ? {} : { color: T.ink },
              }}
            >
              {t(`inbox.category.${c}`)}
              <Box component="span" sx={{
                minWidth: 17, height: 17, px: 0.5, borderRadius: '999px',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.64rem', fontWeight: 700,
                bgcolor: on ? T.brand : '#E2E8F0', color: on ? T.white : T.ink500,
              }}>{categoryCounts[c] ?? 0}</Box>
            </Box>
          );
        })}
      </Box>

      {/* Status tabs (table view) + report + view switch. In Kanban view the
          columns are the statuses, so the status tabs are hidden. */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, overflowX: 'auto', pb: 0.375, borderBottom: `1px solid ${T.lineSoft}` }}
        {...(view === 'table' ? { role: 'tablist', 'aria-label': t('inbox.title') } : {})}
      >
        {view === 'table' && TABS.map((tb) => {
          const active = tab === tb;
          return (
            <Box
              key={tb}
              component="button"
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => { setTab(tb); setPage(0); }}
              sx={{
                position: 'relative', px: 0.25, py: 0.75, cursor: 'pointer', whiteSpace: 'nowrap',
                display: 'flex', alignItems: 'center', gap: 0.625,
                border: 'none', bgcolor: 'transparent', font: 'inherit',
                fontSize: '0.8rem', fontWeight: active ? 800 : 600, transition: 'color .16s',
                color: active ? T.brand : T.ink500,
                '&:hover': { color: T.brand },
                '&::after': active ? {
                  content: '""', position: 'absolute', left: 0, right: 0, bottom: -1, height: '2px',
                  bgcolor: T.brand, borderRadius: '2px',
                } : {},
              }}
            >
              {t(`inbox.tabs.${tb}`)}
              <Box component="span" sx={{
                minWidth: 16, height: 16, px: 0.5, borderRadius: '999px',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.64rem', fontWeight: 700,
                bgcolor: active ? T.brand : '#EEF2F6', color: active ? T.white : T.ink500,
              }}>{counts[tb] ?? 0}</Box>
            </Box>
          );
        })}
        {view === 'table' && <Box sx={{ width: '1px', height: 18, bgcolor: T.line, flexShrink: 0, ml: 0.5 }} />}
        <Button
          size="small" startIcon={<InsertChartRoundedIcon sx={{ fontSize: 18 }} />} onClick={() => setReportOpen(true)}
          sx={{ flexShrink: 0, color: T.brand, fontWeight: 700, textTransform: 'none', borderRadius: '8px', px: 1, '&:hover': { bgcolor: '#F2FAFC' } }}
        >
          {t('inbox.report', 'Raport')}
        </Button>

        <Box sx={{ flex: 1 }} />

        {/* View switch: Tabelë ⇄ Kanban */}
        <Box
          role="radiogroup"
          aria-label={t('inbox.view.label')}
          sx={{ display: 'flex', flexShrink: 0, border: `1px solid ${T.line}`, borderRadius: '9px', overflow: 'hidden', bgcolor: T.white }}
        >
          {[
            { key: 'table', Icon: TableRowsRoundedIcon, label: t('inbox.view.table') },
            { key: 'kanban', Icon: ViewKanbanRoundedIcon, label: t('inbox.view.kanban') },
          ].map((v, i) => {
            const on = view === v.key;
            const VIcon = v.Icon;
            return (
              <Box
                key={v.key}
                component="button"
                type="button"
                role="radio"
                aria-checked={on}
                aria-label={v.label}
                onClick={() => setView(v.key)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, cursor: 'pointer',
                  border: 'none', font: 'inherit',
                  bgcolor: on ? T.brand : 'transparent', color: on ? T.white : T.ink500,
                  borderLeft: i ? `1px solid ${T.line}` : 'none',
                  transition: 'background-color .16s, color .16s',
                  '&:hover': on ? {} : { bgcolor: T.surface, color: T.brand },
                }}
              >
                <VIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, display: { xs: 'none', sm: 'block' } }}>{v.label}</Typography>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Filters — one aligned toolbar; each control keeps a fixed width so the
          row stays tidy and wraps cleanly on narrow screens. */}
      <Box sx={{ p: 1, mb: 1.25, border: `1px solid ${T.line}`, borderRadius: '12px', bgcolor: T.white }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {/* Search — the only flexible-width control */}
          <TextField
            size="small" placeholder={t('inbox.searchPlaceholder')}
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: T.ink400 }} /></InputAdornment> }}
            sx={{ flex: '1 1 220px', minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '9px' } }}
          />

          {/* Type */}
          <FormControl size="small" sx={{ width: { xs: '100%', sm: 176 }, flex: '0 0 auto', '& .MuiOutlinedInput-root': { borderRadius: '9px' } }}>
            <InputLabel>{t('inbox.filter.type', 'Lloji')}</InputLabel>
            <Select
              label={t('inbox.filter.type', 'Lloji')} value={filterType}
              onChange={(e) => { setFilterType(e.target.value); setPage(0); }}
              renderValue={(v) => (v === 'all' ? t('inbox.filter.allTypes', 'Të gjitha llojet') : (typeOptions.find((o) => o.id === v)?.label || v))}
            >
              <MenuItem value="all">{t('inbox.filter.allTypes', 'Të gjitha llojet')}</MenuItem>
              {typeOptions.map((o) => (
                <MenuItem key={o.id} value={o.id} sx={{ gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: o.color, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.84rem' }}>{o.label}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Submitted date range */}
          <TextField
            size="small" type="date" label={t('inbox.filter.from', 'Nga')} InputLabelProps={{ shrink: true }}
            value={filterFrom} onChange={(e) => { setFilterFrom(e.target.value); setPage(0); }}
            inputProps={{ max: filterTo || undefined }}
            sx={{ width: { xs: '48%', sm: 150 }, flex: '0 0 auto', '& .MuiOutlinedInput-root': { borderRadius: '9px' } }}
          />
          <TextField
            size="small" type="date" label={t('inbox.filter.to', 'Deri')} InputLabelProps={{ shrink: true }}
            value={filterTo} onChange={(e) => { setFilterTo(e.target.value); setPage(0); }}
            inputProps={{ min: filterFrom || undefined }}
            sx={{ width: { xs: '48%', sm: 150 }, flex: '0 0 auto', '& .MuiOutlinedInput-root': { borderRadius: '9px' } }}
          />

          {filtersActive && (
            <Button
              size="small" onClick={clearFilters} startIcon={<CloseRoundedIcon sx={{ fontSize: 16 }} />}
              sx={{ color: T.ink500, textTransform: 'none', fontWeight: 600, flex: '0 0 auto', '&:hover': { color: T.ink, bgcolor: T.surface } }}
            >
              {t('inbox.filter.clear', 'Pastro')}
            </Button>
          )}

          <Stack direction="row" spacing={0.625} alignItems="center" sx={{ color: T.ink400, ml: 'auto', flex: '0 0 auto', pr: 0.5 }}>
            <FilterListRoundedIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: '0.74rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
              {(view === 'kanban' ? boardItems.length : visible.length)} {t('inbox.filter.results', 'rezultate')}
            </Typography>
          </Stack>
        </Box>
      </Box>

      {/* Table or Kanban */}
      {view === 'table' ? (
        <RequestTable
          rows={visible} loading={loading} ctx={ctx} busy={busy}
          page={page} rpp={rpp} onPage={setPage} onRpp={(v) => { setRpp(v); setPage(0); }}
          sortBy={sortBy} sortDir={sortDir} onSort={onSort}
          onOpen={(uid) => setSelectedUid(uid)} t={t}
        />
      ) : loading ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
          {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={220} sx={{ borderRadius: '14px' }} />)}
        </Box>
      ) : (
        <KanbanBoard items={boardItems} ctx={ctx} busy={busy}
          onOpen={(uid) => setSelectedUid(uid)} t={t} />
      )}

      {/* Detail dialog */}
      <Dialog
        open={!!selected} onClose={() => setSelectedUid(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', height: { xs: '100%', sm: '86vh' }, m: { xs: 0, sm: 2 } } }}
        fullScreen={!isWide}
      >
        {selected && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <DetailPanel item={selected} ctx={ctx} busy={busy} onAction={runAction} onBack={() => setSelectedUid(null)} t={t} />
          </Box>
        )}
      </Dialog>

      {/* Report dialog */}
      <ApprovalReport open={reportOpen} onClose={() => setReportOpen(false)} data={reportData}
        typeLabel={reportTypeLabel} initialFrom={filterFrom} initialTo={filterTo} t={t} />

      <CreateRequestDialog
        open={!!createType}
        type={createType}
        title={createType ? t(`inbox.types.${createType.id}`) : ''}
        ctx={ctx}
        workers={workers}
        onClose={() => setCreateType(null)}
        onCreated={async () => {
          setCreateType(null);
          setToast({ open: true, msg: t('inbox.create.success'), sev: 'success' });
          await load();
        }}
      />

      <Snackbar open={toast.open} autoHideDuration={2600} onClose={() => setToast((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast.sev} variant="filled" sx={{ fontWeight: 600 }}>{toast.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
